import { useEffect, useMemo, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { supabase } from "@/integrations/supabase/client";
import { CalendarDays, Loader2 } from "lucide-react";
import { ROOM_COUNT, BOAT_COUNT } from "@/lib/lodge-inventory";

type Night = { night: string; rooms_free: number; boats_free: number };

function parseISODate(s: string) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function toISO(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

export function GuestAvailabilityCalendar() {
  const [rows, setRows] = useState<Night[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [selected, setSelected] = useState<Date | undefined>();
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const today = new Date();
      const start = toISO(new Date(today.getFullYear(), today.getMonth(), 1));
      const end = toISO(new Date(today.getFullYear(), today.getMonth() + 8, 0));
      const { data, error } = await supabase.rpc("public_availability", {
        _start: start,
        _end: end,
      });
      if (cancelled) return;
      if (error) {
        setFailed(true);
        setRows([]);
      } else {
        setRows((data ?? []) as Night[]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { byKey, full, limited } = useMemo(() => {
    const map = new Map<string, Night>();
    const fullDays: Date[] = [];
    const limitedDays: Date[] = [];
    (rows ?? []).forEach((r) => {
      const key = r.night.slice(0, 10);
      map.set(key, r);
      if (r.rooms_free <= 0 || r.boats_free <= 0) fullDays.push(parseISODate(key));
      else if (r.rooms_free < ROOM_COUNT || r.boats_free < BOAT_COUNT)
        limitedDays.push(parseISODate(key));
    });
    return { byKey: map, full: fullDays, limited: limitedDays };
  }, [rows]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() + 8);

  const picked = selected ? byKey.get(toISO(selected)) : undefined;

  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
      <div className="flex items-center gap-2">
        <CalendarDays className="h-5 w-5 text-[var(--color-ember)]" />
        <h2 className="font-display text-2xl">Who&apos;s in — nightly availability</h2>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Nights already spoken for by confirmed stays, plus how many rooms and boats
        are still open. No guest details are shown.
      </p>

      {rows === null ? (
        <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading calendar…
        </div>
      ) : failed ? (
        <p className="mt-6 text-sm text-muted-foreground">
          Availability isn&apos;t loading right now — send your dates below and
          we&apos;ll confirm what&apos;s open.
        </p>
      ) : (
        <div className="mt-4 grid gap-6 md:grid-cols-[auto,1fr] md:items-start">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={setSelected}
            month={month}
            onMonthChange={setMonth}
            fromDate={today}
            toDate={maxDate}
            modifiers={{ full, limited }}
            modifiersClassNames={{
              full: "bg-destructive/20 text-destructive font-semibold",
              limited: "bg-[var(--color-ember)]/20 text-[var(--color-ember)] font-semibold",
            }}
            className="p-0 pointer-events-auto"
          />

          <div className="text-sm">
            {picked ? (
              <div className="rounded-lg border border-border bg-background p-4">
                <p className="font-medium">
                  {selected?.toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
                <p className="mt-2 text-muted-foreground">
                  {picked.rooms_free} of {ROOM_COUNT} rooms open · {picked.boats_free} of{" "}
                  {BOAT_COUNT} boats open
                </p>
              </div>
            ) : selected ? (
              <div className="rounded-lg border border-border bg-background p-4">
                <p className="font-medium">
                  {selected.toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
                <p className="mt-2 text-muted-foreground">
                  All {ROOM_COUNT} rooms and {BOAT_COUNT} boats open.
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground">
                Pick a night to see what&apos;s still open.
              </p>
            )}

            <div className="mt-4 grid gap-2 text-xs">
              <span className="inline-flex items-center gap-2">
                <span className="h-3 w-3 rounded-sm border border-border bg-background" />
                Wide open
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-3 w-3 rounded-sm bg-[var(--color-ember)]/40" />
                Partly booked
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-3 w-3 rounded-sm bg-destructive/40" />
                Lodge full
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
